import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { formatEUR } from '../utils/formatCurrency';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  CalendarBlank, Users, Flask, Buildings,
  Package, GearSix, ClipboardText,
  Warning, WarningCircle, Info, ArrowRight, CaretDown,
  Thermometer, Timer, ShieldCheck, FirstAid, FirstAidKit,
  CurrencyEur, MicrosoftExcelLogo, FileCsv, X, ArrowCounterClockwise, Archive, Stethoscope
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import UpcomingStudiesInterestsDialog from '../components/dashboard/UpcomingStudiesInterestsDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priorityConfig = {
  critical: { color: '#DC2626', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', Icon: WarningCircle, labelEs: 'Criticas', labelEn: 'Critical' },
  high: { color: '#EA580C', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', Icon: Warning, labelEs: 'Altas', labelEn: 'High' },
  medium: { color: '#D97706', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', Icon: Warning, labelEs: 'Medias', labelEn: 'Medium' },
  low: { color: '#6B7280', bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-600', Icon: Info, labelEs: 'Bajas', labelEn: 'Low' },
};

const moduleConfig = {
  warehouse: { Icon: Package, labelEs: 'Almacen', labelEn: 'Warehouse', color: '#B45309' },
  volunteers: { Icon: Users, labelEs: 'Voluntarios', labelEn: 'Volunteers', color: '#1f3e80' },
  agenda: { Icon: CalendarBlank, labelEs: 'Agenda', labelEn: 'Agenda', color: '#4A6E8C' },
  studies: { Icon: Flask, labelEs: 'Estudios', labelEn: 'Studies', color: '#7C3AED' },
};

const typeIcons = {
  temperature_out_of_range: Thermometer,
  sample_expired: Timer,
  sample_expiring: Timer,
  consent_missing_all: ShieldCheck,
  consent_missing_partial: ShieldCheck,
  appointment_no_show: WarningCircle,
  appointments_today: CalendarBlank,
  appointments_tomorrow: CalendarBlank,
  ie_pending: FirstAid,
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alertsData, setAlertsData] = useState({ alerts: [], counts: {}, total: 0 });
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  // URS-F320: Mis estudios (asignados al usuario via staff)
  const [myStudies, setMyStudies] = useState(null);
  // URS-F233: Pago a Voluntarios dialog
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const [paymentsDate, setPaymentsDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsData, setPaymentsData] = useState(null);

  const loadPayments = async () => {
    if (!paymentsDate) {
      toast.error(lang === 'es' ? 'Selecciona una fecha' : 'Select a date');
      return;
    }
    setPaymentsLoading(true);
    setPaymentsData(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/payments/completed-volunteers`, {
        params: { date: paymentsDate },
        withCredentials: true
      });
      setPaymentsData(data);
      if (!data.by_study?.length) {
        toast.info(lang === 'es' ? 'No hay voluntarios que cumplan el criterio en esa fecha' : 'No volunteers match for that date');
      }
    } catch (err) {
      toast.error(errText(err, lang === 'es' ? 'Error al cargar listado' : 'Error loading listing'));
    } finally {
      setPaymentsLoading(false);
    }
  };

  const downloadPayments = async (fmt) => {
    if (!paymentsDate) return;
    try {
      const response = await axios.get(`${API_URL}/api/payments/completed-volunteers/export.${fmt}`, {
        // URS-F437: cache-buster _ts para forzar al navegador a saltarse
        // cualquier respuesta cacheada previa con la misma fecha.
        params: { date: paymentsDate, _ts: Date.now() },
        withCredentials: true,
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache' },
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pago_voluntarios_${paymentsDate}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(lang === 'es' ? `Error al descargar ${fmt.toUpperCase()}` : `Error downloading ${fmt.toUpperCase()}`);
    }
  };

  // URS-F384: Liquidacion a Profesionales dialog
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [settlementPros, setSettlementPros] = useState([]);
  const [settlementProId, setSettlementProId] = useState('');
  const _firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
  const _today = () => new Date().toISOString().slice(0, 10);
  const [settlementFrom, setSettlementFrom] = useState(_firstOfMonth());
  const [settlementTo, setSettlementTo] = useState(_today());
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementData, setSettlementData] = useState(null);

  const openSettlement = async () => {
    setSettlementOpen(true);
    setSettlementData(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/settlements/professionals`, { withCredentials: true });
      setSettlementPros(data || []);
      if ((data || []).length && !settlementProId) setSettlementProId(data[0].id);
    } catch (err) {
      toast.error(errText(err, lang === 'es' ? 'Error al cargar profesionales' : 'Error loading professionals'));
    }
  };

  const loadSettlement = async () => {
    if (!settlementProId) { toast.error(lang === 'es' ? 'Selecciona un profesional' : 'Select a professional'); return; }
    if (!settlementFrom || !settlementTo) { toast.error(lang === 'es' ? 'Selecciona el periodo' : 'Select a period'); return; }
    if (settlementFrom > settlementTo) { toast.error(lang === 'es' ? 'El "Desde" debe ser anterior o igual al "Hasta"' : 'From must be earlier than or equal to To'); return; }
    setSettlementLoading(true);
    setSettlementData(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/settlements/monthly`, {
        params: { professional_id: settlementProId, from_date: settlementFrom, to_date: settlementTo },
        withCredentials: true,
      });
      setSettlementData(data);
      if (!data.by_study?.length) toast.info(lang === 'es' ? 'Sin visitas terminadas para este profesional en el periodo' : 'No completed visits in the selected period');
    } catch (err) {
      toast.error(errText(err, lang === 'es' ? 'Error al generar liquidacion' : 'Error generating settlement'));
    } finally {
      setSettlementLoading(false);
    }
  };

  const downloadSettlement = async () => {
    if (!settlementProId || !settlementFrom || !settlementTo) return;
    try {
      const response = await axios.get(`${API_URL}/api/settlements/monthly/export.xlsx`, {
        params: { professional_id: settlementProId, from_date: settlementFrom, to_date: settlementTo },
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const pro = settlementPros.find(p => p.id === settlementProId);
      const proSafe = (pro?.name || 'profesional').replace(/\s+/g, '_');
      a.download = `liquidacion_${proSafe}_${settlementFrom}_${settlementTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(lang === 'es' ? 'Error al descargar Excel' : 'Error downloading Excel');
    }
  };

  const [aeData, setAeData] = useState({ studies: [], total: 0 });
  const [aeOpen, setAeOpen] = useState(false);
  const [expandedAeStudy, setExpandedAeStudy] = useState({});
  const [expandedAeItem, setExpandedAeItem] = useState({});

  // URS-F272: Dismiss alerts (admin only)
  const isAdmin = (user?.roles || []).includes('admin');
  const [dismissTarget, setDismissTarget] = useState(null); // alert object pending dismiss
  const [dismissReason, setDismissReason] = useState('');
  const [dismissBusy, setDismissBusy] = useState(false);
  const [dismissedDialogOpen, setDismissedDialogOpen] = useState(false);
  const [dismissedItems, setDismissedItems] = useState([]);
  const [dismissedLoading, setDismissedLoading] = useState(false);

  const refreshAlerts = () => {
    axios.get(`${API_URL}/api/alerts`, { withCredentials: true })
      .then(r => setAlertsData(r.data))
      .catch(() => {});
  };

  const openDismissDialog = (alert) => {
    setDismissTarget(alert);
    setDismissReason('');
  };
  const closeDismissDialog = () => {
    setDismissTarget(null);
    setDismissReason('');
  };
  const confirmDismiss = async () => {
    if (!dismissTarget) return;
    setDismissBusy(true);
    try {
      await axios.post(`${API_URL}/api/alerts/dismiss`, {
        alert_id: dismissTarget.id,
        reason: dismissReason,
        alert_snapshot: {
          type: dismissTarget.type,
          module: dismissTarget.module,
          priority: dismissTarget.priority,
          title_es: dismissTarget.title_es,
          title_en: dismissTarget.title_en,
          description_es: dismissTarget.description_es,
          description_en: dismissTarget.description_en,
        },
      }, { withCredentials: true });
      toast.success(lang === 'es' ? 'Alerta descartada' : 'Alert dismissed');
      closeDismissDialog();
      refreshAlerts();
    } catch (err) {
      toast.error(errText(err, lang === 'es' ? 'Error al descartar' : 'Error dismissing'));
    } finally {
      setDismissBusy(false);
    }
  };

  const openDismissedList = async () => {
    setDismissedDialogOpen(true);
    setDismissedLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/alerts/dismissed`, { withCredentials: true });
      setDismissedItems(r.data?.items || []);
    } catch (err) {
      toast.error(errText(err, lang === 'es' ? 'Error al cargar descartadas' : 'Error loading dismissed'));
    } finally {
      setDismissedLoading(false);
    }
  };
  const reactivateAlert = async (alertId) => {
    try {
      await axios.delete(`${API_URL}/api/alerts/dismissed/${alertId}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Alerta reactivada' : 'Alert reactivated');
      setDismissedItems(prev => prev.filter(x => x.alert_id !== alertId));
      refreshAlerts();
    } catch (err) {
      toast.error(errText(err, lang === 'es' ? 'Error al reactivar' : 'Error reactivating'));
    }
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }).then(r => setStats(r.data)).catch(() => {});
    refreshAlerts();
    axios.get(`${API_URL}/api/dashboard/adverse-effects`, { withCredentials: true }).then(r => {
      setAeData(r.data);
    }).catch(() => {});
    axios.get(`${API_URL}/api/dashboard/my-studies`, { withCredentials: true })
      .then(r => setMyStudies(r.data?.studies || []))
      .catch(() => setMyStudies([]));
  }, []);

  const kpis = [
    { key: 'total_volunteers', value: stats?.volunteers ?? 0, icon: Users, color: '#1f3e80' },
    { key: 'active_studies', value: stats?.studies ?? 0, icon: Flask, color: '#4A6E8C' },
    { key: 'total_clients', value: stats?.clients ?? 0, icon: Buildings, color: '#C47055' },
    { key: 'total_samples', value: stats?.samples ?? 0, icon: Package, color: '#D99B4A' },
    { key: 'appointments_today', value: stats?.appointments_today ?? 0, icon: CalendarBlank, color: '#3D7A5D' },
  ];

  const quickAccess = [
    { key: 'agenda', path: '/agenda?new=1', icon: CalendarBlank, desc: 'agenda_desc', label: 'new_appointment' },
    { key: 'volunteers', path: '/volunteers/new', icon: Users, desc: 'volunteers_desc', label: 'new_volunteer' },
    { key: 'payments', icon: CurrencyEur, action: 'openPayments',
      allowedRoles: ['admin', 'supervisor_reclutamiento'],
      labelEs: 'Pago a Voluntarios', labelEn: 'Volunteer Payments',
      descEs: 'Generar listado de pagos por fecha de fin de estudio.',
      descEn: 'Generate payment list by study end date.' },
    { key: 'settlement', icon: Stethoscope, action: 'openSettlement',
      allowedRoles: ['admin', 'supervisor_reclutamiento'],
      labelEs: 'Liquidacion a Profesionales', labelEn: 'Professional Settlement',
      descEs: 'Calcular liquidacion mensual por visitas terminadas.',
      descEn: 'Compute monthly settlement per completed visits.' },
    { key: 'upcoming-studies', icon: Flask, action: 'openUpcoming',
      allowedRoles: ['admin', 'supervisor_reclutamiento'],
      labelEs: 'Nuevos Estudios', labelEn: 'Upcoming Studies',
      descEs: 'Nuevos estudios creados y voluntarios interesados.',
      descEn: 'Created upcoming studies and interested volunteers.' },
    { key: 'warehouse', path: '/warehouse', icon: Package, desc: 'warehouse_desc' },
    { key: 'settings', path: '/settings', icon: GearSix, desc: 'settings_desc' },
    { key: 'audit', path: '/audit', icon: ClipboardText, desc: 'audit_desc' },
  ].filter(item => {
    // URS-F386: los accesos rapidos financieros (payments, settlement) solo
    // se muestran a admin y supervisor_reclutamiento.
    if (!item.allowedRoles) return true;
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return item.allowedRoles.some(r => userRoles.includes(r));
  });

  const { alerts, counts, total } = alertsData;

  // Group alerts by module
  const groupedAlerts = {};
  alerts.forEach(a => {
    const mod = a.module || 'other';
    if (!groupedAlerts[mod]) groupedAlerts[mod] = [];
    groupedAlerts[mod].push(a);
  });

  const toggleModule = (mod) => setExpandedModules(prev => ({ ...prev, [mod]: !prev[mod] }));

  // Count critical+high for auto-open
  const urgentCount = (counts.critical || 0) + (counts.high || 0);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome */}
      <div className="animate-fade-in">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-stone-900">
          {t('welcome')}, {user?.name}
        </h1>
        <p className="text-sm text-stone-500 font-body mt-1">{t('role')}: {(user?.roles || [user?.role]).filter(Boolean).map(r => r.charAt(0).toUpperCase() + r.slice(1).replace(/_/g, ' ')).join(', ')}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 animate-stagger">
        {kpis.map(kpi => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.key} data-testid={`kpi-${kpi.key}`} className="bg-white border border-stone-200 rounded-sm shadow-none">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-2.5 rounded-sm" style={{ backgroundColor: kpi.color + '12' }}>
                  <Icon size={22} weight="duotone" style={{ color: kpi.color }} />
                </div>
                <div>
                  <p className="font-mono text-2xl font-semibold text-stone-900">{kpi.value}</p>
                  <p className="text-xs text-stone-500 font-body">{t(kpi.key)}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Alerts Panel */}
      {total > 0 && (
        <div data-testid="alerts-panel">
          <div
            data-testid="alerts-toggle"
            role="button"
            tabIndex={0}
            onClick={() => setAlertsOpen(prev => !prev)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAlertsOpen(prev => !prev); } }}
            className={`w-full cursor-pointer flex items-center justify-between px-4 py-3 bg-white border rounded-sm transition-colors duration-150 ${
              urgentCount > 0 ? 'border-red-200 hover:bg-red-50/50' : 'border-stone-200 hover:bg-stone-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <h2 className="text-base md:text-lg font-heading font-medium text-stone-900">
                {lang === 'es' ? 'Centro de Alertas' : 'Alert Center'}
              </h2>
              <div className="flex items-center gap-2">
                {Object.entries(counts).filter(([, v]) => v > 0).map(([priority, count]) => {
                  const cfg = priorityConfig[priority];
                  if (!cfg) return null;
                  return (
                    <span key={priority} className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-sm ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
                      {count} {lang === 'es' ? cfg.labelEs : cfg.labelEn}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-400 font-mono">{total} total</span>
              {isAdmin && (
                <button
                  data-testid="alerts-dismissed-list-btn"
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openDismissedList(); }}
                  className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-[#1f3e80] px-2 py-1 rounded-sm hover:bg-stone-100 transition-colors"
                  title={lang === 'es' ? 'Ver alertas descartadas' : 'View dismissed alerts'}
                >
                  <Archive size={12} />
                  {lang === 'es' ? 'Descartadas' : 'Dismissed'}
                </button>
              )}
              <CaretDown size={16} className={`text-stone-400 transition-transform duration-200 ${alertsOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>

          {alertsOpen && (
            <div className="mt-3 space-y-3" data-testid="alerts-content">
              {Object.entries(groupedAlerts).map(([mod, modAlerts]) => {
                const mcfg = moduleConfig[mod] || { Icon: Info, labelEs: mod, labelEn: mod, color: '#6B7280' };
                const ModIcon = mcfg.Icon;
                const isExpanded = expandedModules[mod] !== false; // default open
                const modCritical = modAlerts.filter(a => a.priority === 'critical' || a.priority === 'high').length;
                return (
                  <div key={mod} className="border border-stone-200 rounded-sm overflow-hidden" data-testid={`alert-group-${mod}`}>
                    <button
                      onClick={() => toggleModule(mod)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-stone-50 hover:bg-stone-100 transition-colors"
                      data-testid={`alert-group-toggle-${mod}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <ModIcon size={16} style={{ color: mcfg.color }} />
                        <span className="text-sm font-medium text-stone-700">
                          {lang === 'es' ? mcfg.labelEs : mcfg.labelEn}
                        </span>
                        <Badge variant="outline" className="text-[10px] bg-white border-stone-200 text-stone-500 font-mono">
                          {modAlerts.length}
                        </Badge>
                        {modCritical > 0 && (
                          <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200" variant="outline">
                            {modCritical} {lang === 'es' ? 'urgentes' : 'urgent'}
                          </Badge>
                        )}
                      </div>
                      <CaretDown size={14} className={`text-stone-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="divide-y divide-stone-100">
                        {modAlerts.map(alert => {
                          const cfg = priorityConfig[alert.priority] || priorityConfig.low;
                          const TypeIcon = typeIcons[alert.type] || cfg.Icon;
                          return (
                            <div
                              key={alert.id}
                              data-testid={`alert-${alert.id}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => navigate(alert.link)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(alert.link); } }}
                              className={`w-full cursor-pointer flex items-start gap-3 px-4 py-3 text-left hover:bg-stone-50/80 transition-colors group`}
                            >
                              <div className={`mt-0.5 shrink-0 p-1 rounded-sm ${cfg.bg}`}>
                                <TypeIcon size={14} className={cfg.text} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm font-medium ${cfg.text}`}>
                                    {lang === 'es' ? alert.title_es : alert.title_en}
                                  </span>
                                </div>
                                <p className="text-xs text-stone-500 mt-0.5 truncate">
                                  {lang === 'es' ? alert.description_es : alert.description_en}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge variant="outline" className={`text-[9px] ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                                  {lang === 'es' ? cfg.labelEs : cfg.labelEn}
                                </Badge>
                                <ArrowRight size={12} className="text-stone-300 group-hover:text-stone-500 transition-colors" />
                                {isAdmin && (
                                  <button
                                    type="button"
                                    data-testid={`alert-dismiss-${alert.id}`}
                                    onClick={(e) => { e.stopPropagation(); openDismissDialog(alert); }}
                                    title={lang === 'es' ? 'Descartar alerta' : 'Dismiss alert'}
                                    className="p-1 rounded-sm text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <X size={12} weight="bold" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Adverse Effects Global Panel */}
      {aeData.total > 0 && (
        <div data-testid="global-adverse-panel">
          <button
            data-testid="global-adverse-toggle"
            onClick={() => setAeOpen(prev => !prev)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border border-red-200 rounded-sm hover:bg-red-50/50 transition-colors duration-150"
          >
            <div className="flex items-center gap-3">
              <FirstAidKit size={18} className="text-red-600" />
              <h2 className="text-base md:text-lg font-heading font-medium text-red-900">
                {lang === 'es' ? 'Eventos Adversos' : 'Adverse Events'}
              </h2>
              <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-300 font-mono">
                {aeData.total} {lang === 'es' ? 'reportados' : 'reported'}
              </Badge>
              <Badge variant="outline" className="text-[10px] bg-stone-50 text-stone-500 border-stone-200 font-mono">
                {aeData.studies.length} {lang === 'es' ? (aeData.studies.length === 1 ? 'estudio' : 'estudios') : (aeData.studies.length === 1 ? 'study' : 'studies')}
              </Badge>
            </div>
            <CaretDown size={16} className={`text-red-400 transition-transform duration-200 ${aeOpen ? 'rotate-180' : ''}`} />
          </button>

          {aeOpen && (
            <div className="mt-3 space-y-3" data-testid="global-adverse-content">
              {aeData.studies.map(study => {
                const isStudyOpen = expandedAeStudy[study.study_id] !== false;
                return (
                  <div key={study.study_id} className="border border-red-100 rounded-sm overflow-hidden" data-testid={`ae-study-${study.study_id}`}>
                    <button
                      onClick={() => setExpandedAeStudy(p => ({ ...p, [study.study_id]: !isStudyOpen }))}
                      data-testid={`ae-study-toggle-${study.study_id}`}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-red-50/50 hover:bg-red-50 transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <Flask size={14} className="text-red-600" />
                        <span className="text-sm font-medium text-stone-800">{study.study_code}</span>
                        <span className="text-xs text-stone-500">{study.study_title}</span>
                        <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-200 font-mono">
                          {study.effects.length}
                        </Badge>
                      </div>
                      <CaretDown size={14} className={`text-stone-400 transition-transform duration-200 ${isStudyOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isStudyOpen && (
                      <div className="divide-y divide-red-50">
                        {study.effects.map((ae, i) => {
                          const itemKey = `${study.study_id}-${i}`;
                          const isItemOpen = !!expandedAeItem[itemKey];
                          return (
                            <div key={i} data-testid={`ae-item-${itemKey}`} className="bg-white">
                              <button type="button"
                                data-testid={`ae-item-toggle-${itemKey}`}
                                onClick={() => setExpandedAeItem(p => ({ ...p, [itemKey]: !p[itemKey] }))}
                                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-red-50/30 transition-colors text-left"
                              >
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-[10px] bg-stone-50 text-stone-700 border-stone-300 font-mono">{ae.volunteer_code}</Badge>
                                  <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 font-mono">{ae.visit_code}</Badge>
                                  <span className="text-[10px] text-stone-400">{ae.questionnaire}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-[10px] text-stone-400">
                                    {ae.date ? new Date(ae.date).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                                  </span>
                                  <CaretDown size={12} className={`text-stone-400 transition-transform duration-200 ${isItemOpen ? 'rotate-180' : ''}`} />
                                </div>
                              </button>
                              {isItemOpen && (
                                <div className="px-4 pb-3 border-t border-red-50 pt-2 space-y-1.5" data-testid={`ae-detail-${itemKey}`}>
                                  <p className="text-xs font-medium text-stone-700">{ae.question}</p>
                                  <div className="bg-red-50 border border-red-100 rounded-sm px-3 py-2">
                                    <p className="text-xs text-red-900 leading-relaxed">{ae.observation || '-'}</p>
                                  </div>
                                  {ae.signed_by && <p className="text-[10px] text-stone-400 text-right">{ae.signed_by}</p>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Quick Access */}
      <div>
        <h2 className="text-base md:text-lg font-heading font-medium text-stone-900 mb-4">{t('quick_access')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-stagger">
          {quickAccess.map(item => {
            const Icon = item.icon;
            const label = item.labelEs ? (lang === 'es' ? item.labelEs : item.labelEn) : t(item.label || item.key);
            const desc = item.descEs ? (lang === 'es' ? item.descEs : item.descEn) : t(item.desc);
            return (
              <button
                key={item.key}
                data-testid={`quick-access-${item.key}`}
                onClick={() => {
                  if (item.action === 'openPayments') setPaymentsOpen(true);
                  else if (item.action === 'openSettlement') openSettlement();
                  else if (item.action === 'openUpcoming') setUpcomingOpen(true);
                  else navigate(item.path);
                }}
                className="bg-white border border-stone-200 rounded-sm p-5 text-left hover:bg-stone-50 hover:border-stone-300 transition-all duration-150 group"
              >
                <div className="flex items-center gap-3 mb-2.5">
                  <Icon size={22} weight="duotone" className="text-[#1f3e80]" />
                  <span className="text-sm font-medium text-stone-900 group-hover:text-[#1f3e80] transition-colors font-body">
                    {label}
                  </span>
                </div>
                <p className="text-xs text-stone-500 font-body leading-relaxed">{desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* URS-F320: Mis estudios — debajo de Accesos rápidos (movido por petición usuario Feb 2026) */}
      {myStudies !== null && myStudies.length > 0 && (
        <div className="animate-fade-in" data-testid="my-studies-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base md:text-lg font-heading font-medium text-stone-900 flex items-center gap-2">
                <Flask size={18} weight="duotone" className="text-[#7C3AED]" />
                {lang === 'es' ? 'Mis estudios' : 'My studies'}
                <span className="font-mono text-xs text-stone-400 font-normal">({myStudies.length})</span>
              </h2>
              <p className="text-[11px] text-stone-500 mt-0.5">
                {lang === 'es'
                  ? 'Estudios en los que tienes asignado un rol (vista personalizada de tu carga).'
                  : 'Studies where you are assigned to a role (personal workload view).'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {myStudies.map(s => {
              const isActive = (s.status || '').toLowerCase() === 'activo' || (s.status || '').toLowerCase() === 'en_curso';
              const roleLabels = {
                investigador_principal: lang === 'es' ? 'Inv. principal' : 'PI',
                director_estudio: lang === 'es' ? 'Director' : 'Director',
                coordinador: lang === 'es' ? 'Coordinador' : 'Coordinator',
                monitor: lang === 'es' ? 'Monitor' : 'Monitor',
                sondas: lang === 'es' ? 'Sondas' : 'Probes',
                reclutamiento: lang === 'es' ? 'Reclutamiento' : 'Recruitment',
                tecnico: lang === 'es' ? 'Técnico' : 'Technician',
                auxiliar: lang === 'es' ? 'Auxiliar' : 'Assistant',
              };
              return (
                <Card
                  key={s.study_id}
                  className="bg-white border border-stone-200 rounded-sm shadow-none hover:border-violet-300 hover:shadow-sm transition-all duration-150 cursor-pointer group"
                  onClick={() => navigate(`/studies/${s.study_id}`)}
                  data-testid={`my-study-card-${s.study_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-stone-500">{s.code}</span>
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-stone-50 text-stone-500 border-stone-200'}`}
                          >
                            {s.status || '—'}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium text-stone-900 leading-tight line-clamp-2 group-hover:text-violet-700">{s.title || '—'}</p>
                      </div>
                      <ArrowRight size={14} className="text-stone-300 group-hover:text-violet-500 transition-colors mt-1 shrink-0" />
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(s.roles_in_study || []).map(r => (
                        <Badge key={r} variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                          {roleLabels[r] || r}
                        </Badge>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-stone-100">
                      <button
                        type="button"
                        className="flex flex-col items-start hover:bg-stone-50 rounded-sm p-1 -m-1 transition-colors text-left"
                        onClick={(e) => { e.stopPropagation(); navigate(`/agenda?study=${encodeURIComponent(s.study_id)}`); }}
                        data-testid={`my-study-appts-today-${s.study_id}`}
                      >
                        <span className="font-mono text-base font-semibold text-stone-900">{s.appointments_today}</span>
                        <span className="text-[10px] text-stone-500 leading-tight">{lang === 'es' ? 'Citas hoy' : 'Today appts'}</span>
                      </button>
                      <button
                        type="button"
                        className="flex flex-col items-start hover:bg-stone-50 rounded-sm p-1 -m-1 transition-colors text-left"
                        onClick={(e) => { e.stopPropagation(); navigate(`/agenda?study=${encodeURIComponent(s.study_id)}`); }}
                        data-testid={`my-study-appts-week-${s.study_id}`}
                      >
                        <span className="font-mono text-base font-semibold text-stone-900">{s.appointments_week}</span>
                        <span className="text-[10px] text-stone-500 leading-tight">{lang === 'es' ? 'Próximos 7 días' : 'Next 7 days'}</span>
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* URS-F233: Pago a Voluntarios modal */}
      {paymentsOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="payments-modal">
          <div className="bg-white rounded-sm shadow-xl border border-stone-200 w-full max-w-4xl my-8">
            {/* Modal header */}
            <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4 bg-stone-50">
              <div className="flex items-center gap-3">
                <CurrencyEur size={22} weight="duotone" className="text-[#1f3e80]" />
                <h3 className="text-lg font-medium text-stone-900">{lang === 'es' ? 'Pago a Voluntarios' : 'Volunteer Payments'}</h3>
              </div>
              <button
                data-testid="payments-modal-close"
                onClick={() => { setPaymentsOpen(false); setPaymentsData(null); }}
                className="p-1.5 hover:bg-stone-200 rounded-sm transition-colors"
              >
                <X size={18} className="text-stone-600" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 space-y-5">
              {/* Date picker + Generate button */}
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Fecha de fin de estudio' : 'Study end date'}</label>
                  <input
                    data-testid="payments-date-input"
                    type="date"
                    value={paymentsDate}
                    onChange={e => setPaymentsDate(e.target.value)}
                    className="h-10 px-3 border border-stone-300 rounded-sm bg-white text-sm focus:outline-none focus:border-[#1f3e80]"
                  />
                </div>
                <button
                  data-testid="payments-generate-btn"
                  onClick={loadPayments}
                  disabled={paymentsLoading}
                  className="h-10 px-4 bg-[#1f3e80] hover:bg-[#2a4f8a] disabled:opacity-50 text-white rounded-sm text-sm font-medium transition-colors"
                >
                  {paymentsLoading ? (lang === 'es' ? 'Generando...' : 'Generating...') : (lang === 'es' ? 'Generar Listado' : 'Generate List')}
                </button>
                {paymentsData?.by_study?.length > 0 && (
                  <div className="ml-auto flex gap-2">
                    <button
                      data-testid="payments-export-xlsx-btn"
                      onClick={() => downloadPayments('xlsx')}
                      className="h-10 px-3 bg-[#16a34a] hover:bg-[#15803d] text-white rounded-sm text-sm gap-1.5 inline-flex items-center transition-colors"
                    >
                      <MicrosoftExcelLogo size={16} weight="duotone" />
                      {lang === 'es' ? 'Excel' : 'Excel'}
                    </button>
                    <button
                      data-testid="payments-export-csv-btn"
                      onClick={() => downloadPayments('csv')}
                      className="h-10 px-3 bg-stone-700 hover:bg-stone-800 text-white rounded-sm text-sm gap-1.5 inline-flex items-center transition-colors"
                    >
                      <FileCsv size={16} weight="duotone" />
                      CSV
                    </button>
                  </div>
                )}
              </div>

              {/* Results */}
              {paymentsData?.by_study?.length > 0 && (
                <div data-testid="payments-results" className="space-y-4">
                  <div className="flex items-center justify-between px-4 py-2 bg-green-50 border border-green-200 rounded-sm">
                    <span className="text-sm text-stone-700">
                      <strong data-testid="payments-total-volunteers">{paymentsData.total_volunteers}</strong>{' '}
                      {lang === 'es' ? 'voluntarios' : 'volunteers'}
                    </span>
                    <span className="text-base font-semibold text-green-800" data-testid="payments-grand-total">
                      {lang === 'es' ? 'Total: ' : 'Total: '}
                      {formatEUR(paymentsData.grand_total)}
                    </span>
                  </div>
                  {paymentsData.by_study.map(grp => (
                    <div key={grp.study_id} className="border border-stone-200 rounded-sm overflow-hidden" data-testid={`payments-study-${grp.study_code}`}>
                      <div className="px-4 py-2.5 bg-stone-100 border-b border-stone-200 flex items-center justify-between">
                        <span className="text-sm font-semibold text-stone-900">
                          {grp.study_code} — {grp.study_title}
                        </span>
                        <span className="text-xs text-stone-600">
                          {lang === 'es' ? 'Precio/voluntario:' : 'Price/volunteer:'} <strong>{formatEUR(grp.payment_per_volunteer)}</strong> · {lang === 'es' ? 'Ultima visita:' : 'Last visit:'} <strong>{grp.last_visit_code}</strong>
                        </span>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-stone-50 text-stone-600">
                            <th className="px-4 py-2 text-left font-medium">{lang === 'es' ? 'Nombre' : 'Name'}</th>
                            <th className="px-4 py-2 text-left font-medium">{lang === 'es' ? 'Telefono' : 'Phone'}</th>
                            <th className="px-4 py-2 text-right font-medium">{lang === 'es' ? 'Importe' : 'Amount'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grp.rows.map(r => (
                            <tr key={r.volunteer_id} className="border-t border-stone-200">
                              <td className="px-4 py-2 text-stone-900">{r.volunteer_name}</td>
                              <td className="px-4 py-2 text-stone-700">{r.phone || '—'}</td>
                              <td className="px-4 py-2 text-right text-stone-900 font-medium">{formatEUR(r.amount)}</td>
                            </tr>
                          ))}
                          <tr className="border-t border-stone-200 bg-amber-50">
                            <td colSpan={2} className="px-4 py-2 text-right text-sm font-semibold text-stone-700">{lang === 'es' ? 'Subtotal:' : 'Subtotal:'}</td>
                            <td className="px-4 py-2 text-right text-sm font-bold text-amber-900">{formatEUR(grp.subtotal)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}

              {paymentsData && !paymentsData.by_study?.length && (
                <div className="text-center py-10 text-sm text-stone-500" data-testid="payments-empty">
                  {lang === 'es' ? 'No hay voluntarios con cita de ultima visita Terminada en esa fecha.' : 'No volunteers with completed last-visit appointment on that date.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* URS-F384: Liquidacion a Profesionales modal */}
      {settlementOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="settlement-modal">
          <div className="bg-white rounded-sm shadow-xl border border-stone-200 w-full max-w-5xl my-8">
            <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4 bg-stone-50">
              <div className="flex items-center gap-3">
                <Stethoscope size={22} weight="duotone" className="text-[#1f3e80]" />
                <h3 className="text-lg font-medium text-stone-900">{lang === 'es' ? 'Liquidacion a Profesionales' : 'Professional Settlement'}</h3>
              </div>
              <button
                data-testid="settlement-modal-close"
                onClick={() => { setSettlementOpen(false); setSettlementData(null); }}
                className="p-1.5 hover:bg-stone-200 rounded-sm transition-colors"
              >
                <X size={18} className="text-stone-600" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Selectors */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div className="md:col-span-2 space-y-1.5">
                  <label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Profesional' : 'Professional'}</label>
                  <select
                    data-testid="settlement-professional-select"
                    value={settlementProId}
                    onChange={e => setSettlementProId(e.target.value)}
                    className="h-10 w-full px-3 border border-stone-300 rounded-sm bg-white text-sm focus:outline-none focus:border-[#1f3e80]"
                  >
                    <option value="">{lang === 'es' ? '-- Seleccionar --' : '-- Select --'}</option>
                    {settlementPros.map(p => {
                      const full = [p.name, p.surname1, p.surname2].filter(Boolean).join(' ') || p.email;
                      const priceLabel = p.pay_per_visit_enabled && p.pay_per_visit_amount != null
                        ? ` — ${formatEUR(p.pay_per_visit_amount)}/visita`
                        : ` — ${lang === 'es' ? 'sin tarifa' : 'no rate'}`;
                      return (
                        <option key={p.id} value={p.id}>
                          {full} ({p.specialty}){priceLabel}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Desde' : 'From'}</label>
                  <input
                    data-testid="settlement-from-input"
                    type="date"
                    value={settlementFrom}
                    onChange={e => setSettlementFrom(e.target.value)}
                    className="h-10 w-full px-3 border border-stone-300 rounded-sm bg-white text-sm focus:outline-none focus:border-[#1f3e80]"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Hasta' : 'To'}</label>
                  <input
                    data-testid="settlement-to-input"
                    type="date"
                    value={settlementTo}
                    onChange={e => setSettlementTo(e.target.value)}
                    className="h-10 w-full px-3 border border-stone-300 rounded-sm bg-white text-sm focus:outline-none focus:border-[#1f3e80]"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  data-testid="settlement-generate-btn"
                  onClick={loadSettlement}
                  disabled={settlementLoading || !settlementProId}
                  className="h-10 px-4 bg-[#1f3e80] hover:bg-[#2a4f8a] disabled:opacity-50 text-white rounded-sm text-sm font-medium transition-colors"
                >
                  {settlementLoading ? (lang === 'es' ? 'Generando...' : 'Generating...') : (lang === 'es' ? 'Generar Liquidacion' : 'Generate Settlement')}
                </button>
                {settlementData?.by_study?.length > 0 && (
                  <button
                    data-testid="settlement-export-xlsx-btn"
                    onClick={downloadSettlement}
                    className="h-10 px-3 ml-auto bg-[#16a34a] hover:bg-[#15803d] text-white rounded-sm text-sm gap-1.5 inline-flex items-center transition-colors"
                  >
                    <MicrosoftExcelLogo size={16} weight="duotone" />
                    {lang === 'es' ? 'Exportar Excel' : 'Export Excel'}
                  </button>
                )}
              </div>

              {/* Warning if no rate configured */}
              {settlementData?.professional && !settlementData.professional.pay_per_visit_enabled && (
                <div className="px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900" data-testid="settlement-no-rate-warning">
                  {lang === 'es'
                    ? 'Este profesional no tiene "Pago por visita" activado en Configuracion. Los subtotales se muestran a 0,00 €.'
                    : 'This professional has "Pay per visit" disabled in Settings. Subtotals will show 0.00 €.'}
                </div>
              )}

              {/* Results */}
              {settlementData?.by_study?.length > 0 && (
                <div className="space-y-4" data-testid="settlement-results">
                  <div className="flex items-center justify-between px-4 py-2 bg-green-50 border border-green-200 rounded-sm">
                    <span className="text-sm text-stone-700">
                      <strong data-testid="settlement-total-visits">{settlementData.total_visits}</strong>{' '}
                      {lang === 'es' ? 'visitas terminadas' : 'completed visits'}
                    </span>
                    <span className="text-base font-semibold text-green-800" data-testid="settlement-grand-total">
                      {lang === 'es' ? 'Total a liquidar: ' : 'Settlement total: '}
                      {formatEUR(settlementData.grand_total)}
                    </span>
                  </div>
                  {settlementData.by_study.map(grp => (
                    <div key={grp.study_id} className="border border-stone-200 rounded-sm overflow-hidden" data-testid={`settlement-study-${grp.study_code}`}>
                      <div className="px-4 py-2.5 bg-stone-100 border-b border-stone-200 flex items-center justify-between">
                        <span className="text-sm font-semibold text-stone-900">
                          {grp.study_code} — {grp.study_title}
                        </span>
                        <span className="text-xs text-stone-600">
                          {grp.study_total_count} {lang === 'es' ? 'visitas' : 'visits'} · <strong>{formatEUR(grp.study_subtotal)}</strong>
                        </span>
                      </div>
                      {grp.by_visit.map(v => (
                        <div key={v.visit_code || 'sin-visita'} className="border-t border-stone-200">
                          <div className="px-4 py-2 bg-stone-50 flex items-center justify-between">
                            <span className="text-xs font-medium text-stone-700">
                              {v.visit_code ? `${v.visit_code}${v.visit_name ? ` — ${v.visit_name}` : ''}` : (lang === 'es' ? '(sin código de visita)' : '(no visit code)')}
                            </span>
                            <span className="text-xs text-stone-600">
                              {v.count} {lang === 'es' ? 'visitas' : 'visits'} · <strong>{formatEUR(v.subtotal)}</strong>
                            </span>
                          </div>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-white text-stone-500 text-xs">
                                <th className="px-4 py-1.5 text-left font-medium">{lang === 'es' ? 'Fecha' : 'Date'}</th>
                                <th className="px-4 py-1.5 text-left font-medium">{lang === 'es' ? 'Hora' : 'Time'}</th>
                                <th className="px-4 py-1.5 text-left font-medium">{lang === 'es' ? 'Voluntario' : 'Volunteer'}</th>
                                <th className="px-4 py-1.5 text-left font-medium">{lang === 'es' ? 'Codigo' : 'Code'}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {v.appointments.map(a => (
                                <tr key={a.appointment_id} className="border-t border-stone-100">
                                  <td className="px-4 py-1.5 text-stone-800 font-mono text-xs">{a.date}</td>
                                  <td className="px-4 py-1.5 text-stone-700">{a.time || '—'}</td>
                                  <td className="px-4 py-1.5 text-stone-900">{a.volunteer_name || a.volunteer_id}</td>
                                  <td className="px-4 py-1.5 text-stone-600 font-mono text-xs">{a.study_volunteer_code || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {settlementData && !settlementData.by_study?.length && (
                <div className="text-center py-10 text-sm text-stone-500" data-testid="settlement-empty">
                  {lang === 'es'
                    ? 'Sin visitas terminadas atribuibles a este profesional en el periodo seleccionado.'
                    : 'No completed visits attributable to this professional in the selected period.'}
                </div>
              )}

              <p className="text-[11px] text-stone-400 border-t border-stone-100 pt-3">
                {lang === 'es'
                  ? 'Se cuentan las visitas con estado "Terminada" en cualquier estudio donde el profesional figure en el equipo (staff).'
                  : 'Counts appointments with status "Terminada" from any study where the professional is in the staff.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* URS-F272: Confirm dismiss alert dialog (admin only) */}
      <Dialog open={!!dismissTarget} onOpenChange={(o) => { if (!o) closeDismissDialog(); }}>
        <DialogContent data-testid="dismiss-alert-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Descartar alerta' : 'Dismiss alert'}</DialogTitle>
            <DialogDescription>
              {lang === 'es'
                ? 'La alerta dejara de aparecer en el Dashboard hasta que un admin la reactive desde "Descartadas". Si la condicion subyacente cambia (nuevo registro), reaparecera automaticamente.'
                : 'The alert will stop appearing on the Dashboard until an admin reactivates it from "Dismissed". If the underlying condition changes (new record), it will reappear automatically.'}
            </DialogDescription>
          </DialogHeader>
          {dismissTarget && (
            <div className="space-y-3">
              <div className="text-sm bg-stone-50 border border-stone-200 rounded-sm px-3 py-2">
                <div className="font-medium text-stone-800">{lang === 'es' ? dismissTarget.title_es : dismissTarget.title_en}</div>
                <div className="text-xs text-stone-500 mt-1">{lang === 'es' ? dismissTarget.description_es : dismissTarget.description_en}</div>
                <div className="text-[10px] text-stone-400 font-mono mt-1">ID: {dismissTarget.id}</div>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600 block mb-1">
                  {lang === 'es' ? 'Motivo (opcional)' : 'Reason (optional)'}
                </label>
                <Textarea
                  data-testid="dismiss-alert-reason"
                  value={dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                  placeholder={lang === 'es' ? 'Ej: Falso positivo, ya resuelto en sistema externo...' : 'E.g. False positive, already resolved externally...'}
                  rows={3}
                  maxLength={500}
                />
                <div className="text-[10px] text-stone-400 text-right mt-0.5">{dismissReason.length}/500</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDismissDialog} data-testid="dismiss-alert-cancel" disabled={dismissBusy}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={confirmDismiss} data-testid="dismiss-alert-confirm" disabled={dismissBusy} className="bg-red-600 hover:bg-red-700">
              {dismissBusy ? (lang === 'es' ? 'Descartando...' : 'Dismissing...') : (lang === 'es' ? 'Descartar' : 'Dismiss')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URS-F272: Dismissed alerts list dialog (admin only) */}
      <Dialog open={dismissedDialogOpen} onOpenChange={setDismissedDialogOpen}>
        <DialogContent data-testid="dismissed-alerts-dialog" className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Alertas descartadas' : 'Dismissed alerts'}</DialogTitle>
            <DialogDescription>
              {lang === 'es'
                ? 'Listado de alertas archivadas por administradores. Pulsa "Reactivar" para que vuelvan a evaluarse en el Dashboard.'
                : 'List of alerts archived by administrators. Click "Reactivate" to re-evaluate them on the Dashboard.'}
            </DialogDescription>
          </DialogHeader>
          {dismissedLoading ? (
            <div className="py-8 text-center text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>
          ) : dismissedItems.length === 0 ? (
            <div className="py-8 text-center text-sm text-stone-500" data-testid="dismissed-alerts-empty">
              {lang === 'es' ? 'No hay alertas descartadas.' : 'No dismissed alerts.'}
            </div>
          ) : (
            <div className="space-y-2">
              {dismissedItems.map(it => {
                const snap = it.alert_snapshot || {};
                return (
                  <div key={it.alert_id} data-testid={`dismissed-item-${it.alert_id}`} className="border border-stone-200 rounded-sm p-3 bg-stone-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-stone-800">
                          {lang === 'es' ? (snap.title_es || it.alert_id) : (snap.title_en || it.alert_id)}
                        </div>
                        {(snap.description_es || snap.description_en) && (
                          <div className="text-xs text-stone-500 mt-0.5">
                            {lang === 'es' ? snap.description_es : snap.description_en}
                          </div>
                        )}
                        <div className="text-[10px] text-stone-400 font-mono mt-1">ID: {it.alert_id}</div>
                        <div className="text-[11px] text-stone-600 mt-2">
                          <span className="font-medium">{lang === 'es' ? 'Descartada por:' : 'Dismissed by:'}</span> {it.dismissed_by_email || it.dismissed_by_name || '?'}
                          <span className="mx-2 text-stone-300">·</span>
                          <span>{it.dismissed_at ? new Date(it.dismissed_at).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US') : ''}</span>
                        </div>
                        {it.reason && (
                          <div className="text-[11px] text-stone-700 mt-1 italic">&ldquo;{it.reason}&rdquo;</div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`dismissed-reactivate-${it.alert_id}`}
                        onClick={() => reactivateAlert(it.alert_id)}
                        className="shrink-0"
                      >
                        <ArrowCounterClockwise size={12} className="mr-1" />
                        {lang === 'es' ? 'Reactivar' : 'Reactivate'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissedDialogOpen(false)} data-testid="dismissed-alerts-close">
              {lang === 'es' ? 'Cerrar' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpcomingStudiesInterestsDialog open={upcomingOpen} onOpenChange={setUpcomingOpen} lang={lang} />
    </div>
  );
}
